/**
 * Parse the wizard's AI-generated marketing HTML (stored in `pages.content`)
 * into a lightweight structured shape the tenant marketing pages can render as
 * cards instead of dumping it through `dangerouslySetInnerHTML`.
 *
 * The generator emits semantic blocks — a lead `<p>` intro, then `<h2>` section
 * titles each followed by `<p>`/`<ul>` bodies (see
 * functions/src/services/wizard/saas-content-generator.ts). We walk those:
 *   - paragraphs before the first heading become `intro` (the About page is all
 *     paragraphs and no headings — it lands entirely in `intro`);
 *   - each `<h1>`/`<h2>`/`<h3>` starts a `section`, collecting the following
 *     paragraphs as `body` and list items as `bullets`.
 *
 * Only text content is extracted (never re-injected as HTML), so this is also
 * strictly safer than the old raw-HTML render. Callers treat an empty
 * `sections` + empty `intro` as "couldn't structure it" and fall back to a
 * styled prose render of the original HTML.
 */

export interface MarketingSection {
  title: string;
  /** Body paragraphs, in order. */
  body: string[];
  /** Flattened `<li>` text from any lists inside the section. */
  bullets: string[];
}

export interface ParsedMarketing {
  /** Leading paragraphs before the first heading (or all paragraphs if there are no headings). */
  intro: string[];
  sections: MarketingSection[];
}

const EMPTY: ParsedMarketing = { intro: [], sections: [] };

const HEADING_TAGS = new Set(['h1', 'h2', 'h3', 'h4']);
const LIST_TAGS = new Set(['ul', 'ol']);

function collapseWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Structure marketing HTML. Always defensive: returns empty intro+sections for
 * blank input, environments without `DOMParser`, or unparseable markup.
 */
export function parseMarketingSections(html: string | null | undefined): ParsedMarketing {
  if (!html || typeof html !== 'string' || !html.trim()) return { intro: [], sections: [] };
  if (typeof DOMParser === 'undefined') return { intro: [], sections: [] };

  let root: Element | null = null;
  try {
    const doc = new DOMParser().parseFromString(`<main id="rmkt-root">${html}</main>`, 'text/html');
    root = doc.getElementById('rmkt-root');
  } catch {
    return { intro: [], sections: [] };
  }
  if (!root) return EMPTY;

  const intro: string[] = [];
  const sections: MarketingSection[] = [];
  let current: MarketingSection | null = null;

  const addParagraph = (raw: string) => {
    const text = collapseWhitespace(raw);
    if (!text) return;
    if (current) current.body.push(text);
    else intro.push(text);
  };

  for (const el of Array.from(root.children)) {
    const tag = el.tagName.toLowerCase();

    if (HEADING_TAGS.has(tag)) {
      const title = collapseWhitespace(el.textContent || '');
      if (!title) continue;
      current = { title, body: [], bullets: [] };
      sections.push(current);
      continue;
    }

    if (LIST_TAGS.has(tag)) {
      const items = Array.from(el.querySelectorAll('li'))
        .map((li) => collapseWhitespace(li.textContent || ''))
        .filter(Boolean);
      if (!items.length) continue;
      if (current) current.bullets.push(...items);
      // A list before any heading is rare; fold its items into the intro so
      // nothing is silently dropped.
      else items.forEach(addParagraph);
      continue;
    }

    // Paragraphs, blockquotes, divs, etc. — take their text.
    addParagraph(el.textContent || '');
  }

  return { intro, sections };
}

/** True when there's enough structure to render cards rather than fall back to prose. */
export function hasRenderableStructure(parsed: ParsedMarketing): boolean {
  return parsed.sections.length > 0 || parsed.intro.length > 0;
}
